import { useState, useEffect, useRef, useCallback } from 'react';

const SpeechRecognition =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

export const useSpeechRecognition = ({
  onResult,
  onStart,
  onEnd,
  language = 'en-US',
  interimResults = true,
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [interim, setInterim]         = useState('');
  const recognitionRef                = useRef(null);
  const stoppedManually               = useRef(false);

  useEffect(() => {
    setIsSupported(!!SpeechRecognition);
  }, []);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      stoppedManually.current = true;
      recognitionRef.current.stop();
    }
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognition) {
      console.warn('SpeechRecognition not supported in this browser.');
      return;
    }
    if (isListening) return;

    const recognition = new SpeechRecognition();
    recognitionRef.current  = recognition;
    stoppedManually.current = false;

    recognition.lang            = language;
    recognition.continuous      = false;
    recognition.interimResults  = interimResults;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setInterim('');
      onStart?.();
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript   = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      setInterim(interimTranscript);
      if (finalTranscript.trim()) {
        onResult?.(finalTranscript.trim());
        setInterim('');
      }
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') {
        console.error('STT error:', event.error);
      }
      setIsListening(false);
      setInterim('');
      onEnd?.();
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterim('');
      onEnd?.();
    };

    recognition.start();
  }, [isListening, language, interimResults, onResult, onStart, onEnd]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        stoppedManually.current = true;
        recognitionRef.current.abort();
      }
    };
  }, []);

  return { start, stop, isListening, isSupported, interim };
};
